"use client";

// Instalación manual adaptada de beUI: https://beui.dev/components/blocks/command-palette
// Copyright (c) 2026 Saurabh Chauhan. Licencia: ../../licenses/beui-MIT.txt.
// Conserva fuzzy filter, agrupación, cursor por identidad y fila animada.
// Klavia usa CSS Modules, tokens propios y un dialog nativo para foco e inert.
import { motion, useReducedMotion } from "motion/react";
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { Icon, type IconName } from "../core/Icon";
import { useRowCursor } from "../../lib/use-row-cursor";
import s from "./CommandPalette.module.css";

export type CommandItem = {
  id: string;
  label: string;
  group?: string;
  keywords?: string[];
  icon?: IconName;
  badge?: ReactNode;
  onSelect: () => void;
};

export interface CommandPaletteProps {
  items: CommandItem[];
  shortcut?: string;
  placeholder?: string;
  emptyMessage?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onQueryChange?: (query: string) => void;
  loading?: boolean;
  message?: string;
}

function normalizar(value: string) {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase().trim();
}

function fuzzyMatch(needle: string, hay: string) {
  needle = normalizar(needle);
  if (!needle) return true;
  hay = normalizar(hay);
  let i = 0;
  for (const ch of hay) {
    if (ch === needle[i]) i++;
    if (i === needle.length) return true;
  }
  return false;
}

export function CommandPalette({ open: controlledOpen, onOpenChange, shortcut = "k", ...props }: CommandPaletteProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = useCallback((value: boolean) => {
    if (controlledOpen === undefined) setInternalOpen(value);
    onOpenChange?.(value);
  }, [controlledOpen, onOpenChange]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.isComposing || e.repeat || e.altKey || e.shiftKey) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === shortcut.toLowerCase()) {
        // No interrumpir otro diálogo (por ejemplo, un formulario sin guardar).
        if (!open && document.querySelector("dialog[open]")) return;
        e.preventDefault();
        setOpen(!open);
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, shortcut, setOpen]);

  // Desmontar al cerrar restablece consulta y cursor al volver a abrir.
  return open ? <PaletteContent {...props} onClose={() => setOpen(false)} /> : null;
}

function PaletteContent({ items, placeholder = "Buscar páginas o propiedades…", emptyMessage = "No encontramos resultados. Probá con otra búsqueda.", onQueryChange, loading, message, onClose }: Omit<CommandPaletteProps, "open" | "onOpenChange" | "shortcut"> & { onClose: () => void }) {
  const [query, setQuery] = useState("");
  const uid = useId();
  const reduce = useReducedMotion();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    el.showModal();
    inputRef.current?.focus();
    document.body.style.overflow = "hidden";
    return () => {
      el.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
  }, []);

  const filtered = useMemo(() => items.filter((it) =>
    [it.label, it.group ?? "", ...(it.keywords ?? [])].some((hay) => fuzzyMatch(query, hay)),
  ), [items, query]);
  const grouped = useMemo(() => {
    const groups = new Map<string, CommandItem[]>();
    for (const item of filtered) {
      const group = item.group ?? "Resultados";
      const entries = groups.get(group) ?? [];
      entries.push(item);
      groups.set(group, entries);
    }
    return [...groups.entries()];
  }, [filtered]);
  const rows = useMemo(() => grouped.flatMap(([, list]) => list), [grouped]);
  const { activeIndex: active, moveTo, moveActive } = useRowCursor(rows, query);
  const hasIcons = items.some((it) => it.icon);

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active, rows]);

  function select(item: CommandItem) {
    onClose();
    item.onSelect();
  }

  return <dialog
    ref={dialogRef}
    aria-label="Buscar en Klavia"
    aria-modal="true"
    className={s.dialog}
    onCancel={(e) => { e.preventDefault(); onClose(); }}
    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
    <motion.div
      className={s.panel}
      initial={{ opacity: 0, y: reduce ? 0 : -8, scale: reduce ? 1 : 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: reduce ? 0 : 0.22, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className={s.cabecera}>
        <Icon name="search" size={18} />
        <input
          ref={inputRef}
          className={s.campo}
          role="combobox"
          aria-label="Buscar páginas o propiedades"
          aria-expanded="true"
          aria-autocomplete="list"
          aria-controls={`${uid}-list`}
          aria-activedescendant={rows[active] ? `${uid}-opt-${active}` : undefined}
          autoComplete="off"
          spellCheck={false}
          maxLength={120}
          value={query}
          placeholder={placeholder}
          onChange={(e) => { setQuery(e.target.value); onQueryChange?.(e.target.value); }}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing) return;
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault(); moveActive(e.key === "ArrowDown" ? 1 : -1);
            } else if (e.key === "Enter") {
              e.preventDefault(); if (rows[active]) select(rows[active]);
            }
          }}
        />
        <button className={s.cerrar} type="button" onClick={onClose} aria-label="Cerrar búsqueda">Esc</button>
      </div>
      <div ref={listRef} id={`${uid}-list`} role="listbox" aria-label="Resultados" className={s.lista} aria-busy={loading || undefined}>
        {grouped.map(([group, list], groupIndex) => <div role="group" aria-labelledby={`${uid}-group-${groupIndex}`} key={group}>
          <div id={`${uid}-group-${groupIndex}`} className={s.grupo}>{group}</div>
          {list.map((item) => {
            const index = rows.indexOf(item);
            const isActive = index === active;
            return <button
              key={item.id}
              id={`${uid}-opt-${index}`}
              role="option"
              aria-label={item.label}
              aria-description={typeof item.badge === "string" ? item.badge : undefined}
              type="button"
              tabIndex={-1}
              aria-selected={isActive}
              data-index={index}
              className={s.fila}
              onPointerMove={(e) => { if (e.pointerType === "mouse") moveTo(item.id); }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => select(item)}
            >
              {isActive ? <motion.span className={s.activa} layoutId={`${uid}-active`} transition={{ duration: reduce ? 0 : 0.14, ease: [0.2, 0, 0.2, 1] }} /> : null}
              {hasIcons ? <span className={s.icono}>{item.icon ? <Icon name={item.icon} size={18} /> : null}</span> : null}
              <span className={s.label}>{item.label}</span>
              {item.badge ? <span className={s.badge}>{item.badge}</span> : null}
              {isActive ? <span className={s.enter} aria-hidden="true">↵</span> : null}
            </button>;
          })}
        </div>)}
      </div>
      <div role="status" aria-live="polite" className={s.estado} hidden={!loading && !message && rows.length > 0}>
        {loading ? "Buscando propiedades…" : message || (rows.length === 0 ? emptyMessage : "")}
      </div>
      <div className={s.pie} aria-hidden="true"><span>↑ ↓ Navegar</span><span>↵ Abrir</span><span>Esc Cerrar</span></div>
    </motion.div>
  </dialog>;
}
